import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, type Agent } from '../api';
import { Page, Card, Button, Field, Input, Select } from '../ui';

export function Integrations() {
  const { slug } = useParams<{ slug: string }>();
  const [agents, setAgents] = useState<Agent[]>([]);

  // Config do bot
  const [configured, setConfigured] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfgMsg, setCfgMsg] = useState<string | null>(null);

  // Pareamento
  const [agentId, setAgentId] = useState<string>('__router__');
  const [code, setCode] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    api.listAgents(slug).then(({ agents }) => setAgents(agents.filter((a) => a.enabled && !a.isRouter))).catch(() => undefined);
    api
      .getTelegramConfig(slug)
      .then((c) => {
        setConfigured(c.configured);
        setEnabled(c.enabled);
        setBotUsername(c.botUsername);
      })
      .catch(() => undefined);
  }, [slug]);

  async function saveConfig() {
    if (!slug || !token.trim() || savingCfg) return;
    setSavingCfg(true);
    setCfgMsg(null);
    setErr(null);
    try {
      const c = await api.saveTelegramConfig(slug, { botToken: token.trim(), enabled: true });
      setConfigured(true);
      setEnabled(c.enabled);
      setBotUsername(c.botUsername);
      setToken('');
      setCfgMsg(c.botUsername ? `Conectado a @${c.botUsername}. Pode parear conversas abaixo.` : 'Bot conectado.');
    } catch (e) {
      const m = (e as { error?: string }).error;
      setErr(m === 'invalid_token' ? 'Token inválido. Confira com o @BotFather.' : 'Falha ao salvar o bot.');
    } finally {
      setSavingCfg(false);
    }
  }

  async function disconnect() {
    if (!slug) return;
    if (!confirm('Desconectar o bot do Telegram deste projeto?')) return;
    try {
      await api.deleteTelegramConfig(slug);
      setConfigured(false);
      setEnabled(false);
      setBotUsername(null);
      setCode(null);
      setCfgMsg(null);
    } catch {
      setErr('Falha ao desconectar.');
    }
  }

  async function generate() {
    if (!slug || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const body = agentId === '__router__' ? {} : { agentId };
      const { code, instructions } = await api.pairTelegram(slug, body);
      setCode(code);
      setInstructions(instructions);
    } catch (e) {
      const m = (e as { error?: string }).error;
      setErr(m === 'telegram_not_configured' ? 'Configure o bot antes de parear.' : 'Falha ao gerar código.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Integrações" subtitle="Conecte canais de mensageria aos agentes do projeto.">
      {err && <div className="chat-error">{err}</div>}
      <div className="agent-grid">
        <Card className="agent-card">
          <div className="agent-card-name">📨 Telegram</div>

          {!configured ? (
            <>
              <div className="agent-card-desc">
                Crie um bot com o <strong>@BotFather</strong> no Telegram, copie o token e cole aqui.
                Funciona em localhost — não precisa de URL pública nem variáveis de ambiente.
              </div>
              <Field label="Token do bot">
                <Input
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="123456789:ABCdef..."
                  autoComplete="off"
                />
              </Field>
              <Button variant="primary" onClick={saveConfig} disabled={!token.trim() || savingCfg}>
                {savingCfg ? 'conectando…' : 'Conectar bot'}
              </Button>
            </>
          ) : (
            <>
              <div className="agent-card-desc">
                Conectado{botUsername ? ` a @${botUsername}` : ''}. {enabled ? 'Ativo.' : 'Desativado.'}
              </div>

              <Field label="Agente padrão da conversa">
                <Select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                  <option value="__router__">Recepcionista (escolhe o agente)</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="primary" onClick={generate} disabled={busy}>
                  {busy ? 'gerando…' : 'Gerar código de pareamento'}
                </Button>
                <Button variant="ghost" onClick={disconnect}>
                  desconectar
                </Button>
              </div>

              {code && (
                <div className="agent-editor-msg" style={{ marginTop: 12 }}>
                  <div>
                    Código: <strong style={{ fontFamily: 'ui-monospace, monospace' }}>{code}</strong>
                  </div>
                  <div style={{ marginTop: 6 }}>{instructions}</div>
                </div>
              )}
            </>
          )}

          {cfgMsg && <div className="agent-editor-msg" style={{ marginTop: 12 }}>{cfgMsg}</div>}
        </Card>
      </div>
    </Page>
  );
}
